import { Grid, Switch } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { Declaration, DeclarationHighValueGroupType, EnvironmentCountries, TransactionTypes } from '@e-origin/shared';

import { EucdmCode, HideElementFor, MultiLangCodes, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { BeforeSendingFormData } from '../beforeSendingBatchFormIntializer';
import { TransportDetails } from './TransportDetails';

interface FinalizeTransportProps {
  form: UseFormReturnType<BeforeSendingFormData>;
  declaration: Declaration;
  disabled: boolean;
}

export const FinalizeTransport: React.FC<FinalizeTransportProps> = ({ form, declaration, disabled }) => {
  const { data: settings } = useSettings();
  const isExport = !!declaration.generalInfo.isExport;
  const declarationGroup = declaration?.generalInfo?.group as DeclarationHighValueGroupType;
  const isB2BTransaction = [TransactionTypes.B2B_HV, TransactionTypes.B2B_EXPORT, TransactionTypes.B2B2C_HV, undefined].includes(declaration.transactionType);
  const isNetherlands = settings.country === EnvironmentCountries.NL;

  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport,
  });

  return (
    <>
      {isB2BTransaction && (
        <>
          <Grid mb="sm">
            <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.BE}>
              <Grid.Col span={6}>
                <EucdmCode path="consignment.countryOfDispatch">
                  <MultiLangCodes
                    {...form.getInputProps('consignment.countryOfDispatch')}
                    label="Country of Dispatch"
                    code="CL199"
                    disabled={disabled || !form.values.consignment.countryOfDispatchHeader}
                    rightSection={
                      <Switch
                        {...form.getInputProps('consignment.countryOfDispatchHeader')}
                        checked={form.values.consignment.countryOfDispatchHeader}
                        size="xs"
                      />
                    }
                    size="lg"
                    data-pw="country-of-dispatch-input"
                    system={codelistSystems}
                  />
                </EucdmCode>
              </Grid.Col>
            </HideElementFor>

            {isExport && (
              <Grid.Col span={6}>
                <EucdmCode path="consignment.countryOfExport">
                  <MultiLangCodes
                    {...form.getInputProps('consignment.countryOfExport')}
                    label="Country of Export"
                    code="CL008"
                    disabled={disabled || !form.values.consignment.countryOfExportHeader}
                    rightSection={
                      <Switch {...form.getInputProps('consignment.countryOfExportHeader')} checked={form.values.consignment.countryOfExportHeader} size="xs" />
                    }
                    size="lg"
                    data-pw="country-of-export-input"
                    system={codelistSystems}
                  />
                </EucdmCode>
              </Grid.Col>
            )}
          </Grid>

          <Grid mb="sm">
            <Grid.Col span={6}>
              <EucdmCode path="consignment.countryOfDestination">
                <MultiLangCodes
                  {...form.getInputProps('consignment.countryOfDestination')}
                  label="Country of Destination"
                  code={declaration.generalInfo.isExport ? 'CL008' : 'CL010'}
                  disabled={disabled || !form.values.consignment.destinationHeader}
                  rightSection={
                    <Switch {...form.getInputProps('consignment.destinationHeader')} checked={form.values.consignment.destinationHeader} size="xs" />
                  }
                  size="lg"
                  data-pw="country-of-destination-input"
                  system={codelistSystems}
                />
              </EucdmCode>
            </Grid.Col>

            {!isNetherlands && !isExport && declaration.transactionType !== TransactionTypes.B2B_MULTIPLE && (
              <Grid.Col span={6}>
                <EucdmCode path="consignment.regionOfDestination">
                  <TextInput
                    {...form.getInputProps('consignment.regionOfDestination')}
                    label="Region of Destination"
                    disabled={disabled || !form.values.consignment.destinationHeader}
                    rightSection={
                      <Switch {...form.getInputProps('consignment.destinationHeader')} checked={form.values.consignment.destinationHeader} size="xs" />
                    }
                    size="lg"
                    data-pw="region-of-destination-input"
                  />
                </EucdmCode>
              </Grid.Col>
            )}
          </Grid>

          {isExport && (
            <Grid mb="sm">
              <Grid.Col span={6}>
                <TextInput
                  {...form.getInputProps('consignment.carrier')}
                  label="Carrier EORI"
                  disabled={disabled}
                  rightSection={<Switch {...form.getInputProps('consignment.carrierHeader')} checked={form.values.consignment.carrierHeader} size="xs" />}
                  size="lg"
                  data-pw="carrier-input"
                />
              </Grid.Col>
            </Grid>
          )}
        </>
      )}

      <TransportDetails form={form} disabled={disabled} isExport={isExport} isB2BTransaction={isB2BTransaction} declarationGroup={declarationGroup} />

      {isB2BTransaction && (
        <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
          <Grid mb="sm">
            <Grid.Col span={4}>
              <EucdmCode path="valuationInformationTaxes.deliveryTerms.incotermCode">
                <MultiLangCodes
                  {...form.getInputProps('valuationInformationTaxes.deliveryTerms.incotermCode')}
                  label="Delivery terms"
                  code="CL090"
                  disabled={disabled}
                  size="lg"
                  data-pw="transport-delivery-terms-input"
                  system={codelistSystems}
                />
              </EucdmCode>
            </Grid.Col>

            <Grid.Col span={4}>
              <EucdmCode path="valuationInformationTaxes.deliveryTerms.location">
                <TextInput
                  data-pw="delivery-terms-place-input"
                  {...form.getInputProps('valuationInformationTaxes.deliveryTerms.location')}
                  label="Delivery terms place"
                  size="lg"
                  disabled={disabled}
                />
              </EucdmCode>
            </Grid.Col>

            <Grid.Col span={4}>
              <EucdmCode path="valuationInformationTaxes.deliveryTerms.country">
                <MultiLangCodes
                  {...form.getInputProps('valuationInformationTaxes.deliveryTerms.country')}
                  label="Delivery terms country"
                  code="CL199"
                  size="lg"
                  disabled={disabled}
                  data-pw="delivery-terms-country-input"
                  system={codelistSystems}
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </HideElementFor>
      )}
    </>
  );
};
